import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, MapPin, Clock, CheckCircle2, Package, Truck, XCircle, Trash2, AlertTriangle, Pencil, RefreshCw, Check } from 'lucide-react';
import { lockBody, unlockBody } from '@/lib/bodyLock';
import { usePedidosStore } from './store';
import { colunaDePedido } from './statusMap';
import Money from '@/components/Money';
import Pill from '@/components/Pill';
import Button from '@/components/Button';
import { formatTicket, horaCurta, tempoRelativo } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useToast } from '@/components/Toast';
import type { StatusBackend } from './types';

export function PedidoDetailDrawer() {
  const id = usePedidosStore((s) => s.selecionado);
  const pedido = usePedidosStore((s) => (id ? s.pedidos[id] : null));
  const fechar = usePedidosStore((s) => s.selecionar);
  const mudar = usePedidosStore((s) => s.mudarStatus);
  const excluir = usePedidosStore((s) => s.excluir);
  const atualizar = usePedidosStore((s) => s.atualizar);
  const toast = useToast();
  // confirmação inline: armazena o id pendente; null = nenhum confirm em andamento
  const [confirmExcluirId, setConfirmExcluirId] = useState<number | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [editandoEndereco, setEditandoEndereco] = useState(false);
  const [enderecoBuffer, setEnderecoBuffer] = useState('');
  const [salvandoEndereco, setSalvandoEndereco] = useState(false);
  const [regeocodificando, setRegeocodificando] = useState(false);
  const [mapaBuffer, setMapaBuffer] = useState('');
  const [salvandoMapa, setSalvandoMapa] = useState(false);

  // reseta TODO estado transitório ao trocar/fechar pedido
  useEffect(() => {
    setConfirmExcluirId(null);
    setExcluindo(false);
    setEditandoEndereco(false);
    setEnderecoBuffer('');
    setSalvandoEndereco(false);
    setRegeocodificando(false);
    setMapaBuffer('');
    setSalvandoMapa(false);
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar(null);
    };
    if (id != null) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id, fechar]);

  const visivel = pedido != null;

  // bloqueia scroll do body enquanto drawer está aberto (ref-counted)
  useEffect(() => {
    if (!visivel) return;
    lockBody();
    return () => unlockBody();
  }, [visivel]);

  const acoes: { label: string; status: StatusBackend; tone: 'primary' | 'default' | 'danger'; Icon: typeof CheckCircle2 }[] = pedido
    ? [
        ...(pedido.status === 'recebido'
          ? [{ label: 'Marcar como pago', status: 'pago' as StatusBackend, tone: 'primary' as const, Icon: CheckCircle2 }]
          : []),
        ...(pedido.status === 'pago'
          ? [{ label: 'Para separação', status: 'em_separacao' as StatusBackend, tone: 'default' as const, Icon: Package }]
          : []),
        ...(pedido.status === 'em_separacao'
          ? [
              { label: 'Marcar entregue', status: 'entregue' as StatusBackend, tone: 'primary' as const, Icon: CheckCircle2 },
              { label: 'Despachar', status: 'em_rota' as StatusBackend, tone: 'default' as const, Icon: Truck },
            ]
          : []),
        ...(pedido.status === 'em_rota'
          ? [{ label: 'Entregue', status: 'entregue' as StatusBackend, tone: 'primary' as const, Icon: CheckCircle2 }]
          : []),
        ...(pedido.status !== 'cancelado' && pedido.status !== 'entregue'
          ? [{ label: 'Cancelar', status: 'cancelado' as StatusBackend, tone: 'danger' as const, Icon: XCircle }]
          : []),
      ]
    : [];

  return createPortal(
    <>
      <div
        onClick={() => fechar(null)}
        className={cn(
          'fixed inset-0 z-[100] transition-opacity duration-slow ease-out-expo',
          visivel ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        style={{ background: 'rgba(7,9,10,0.6)', backdropFilter: 'blur(10px)' }}
      />

      <aside
        className={cn(
          'fixed top-0 right-0 bottom-0 z-[101] w-[480px] max-w-[92vw] bg-elev border-l border-[var(--border-strong)]',
          'transition-transform duration-slow ease-out-expo',
          visivel ? 'translate-x-0' : 'translate-x-full',
        )}
        style={{ boxShadow: '-24px 0 60px rgba(0,0,0,0.55)' }}
        aria-hidden={!visivel}
      >
        {pedido && (
          <div className="h-full flex flex-col">
            <header className="flex items-center justify-between px-s7 py-s5 border-b border-[var(--border-subtle)]">
              <div className="flex flex-col gap-s1">
                <span className="font-mono text-[12px] text-moss-300">{formatTicket(pedido.numero_ticket)}</span>
                <h2 className="font-display text-[28px] leading-tight text-fg-1 tracking-[-0.02em]">
                  {pedido.nome_cliente}
                </h2>
              </div>
              <button
                onClick={() => fechar(null)}
                className="p-s2 rounded-sm border border-transparent text-fg-3 hover:text-fg-1 hover:border-[var(--border)]"
                aria-label="fechar"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-s7 py-s5 flex flex-col gap-s5">
              <div className="flex items-center gap-s3 text-fg-3">
                <Pill tone={statusTone(pedido.status)}>{statusLabel(pedido.status)}</Pill>
                {colunaDePedido(pedido) && (
                  <span className="font-mono text-[11px]">{horaCurta(pedido.criado_em)} · {tempoRelativo(pedido.criado_em)}</span>
                )}
              </div>

              <Bloco label="Itens">
                <ul className="font-body text-[13.5px] text-fg-1 leading-relaxed">
                  {(pedido.itens || []).map((it, i) => (
                    <li key={i}>
                      {it.qtd ? `${it.qtd}× ` : ''}
                      {it.descricao}
                    </li>
                  ))}
                  {(pedido.itens || []).length === 0 && <li className="text-fg-3 italic">Sem itens.</li>}
                </ul>
              </Bloco>

              <Bloco label="Endereço">
                {editandoEndereco ? (
                  <div className="flex flex-col gap-s2">
                    <textarea
                      value={enderecoBuffer}
                      onChange={(e) => setEnderecoBuffer(e.target.value)}
                      autoFocus
                      rows={2}
                      className="bg-input border border-moss-400 rounded-sm px-s4 py-[9px] font-body text-[13px] text-fg-1 outline-none focus:bg-card focus:shadow-[0_0_0_3px_rgba(149,184,134,0.12)] resize-y min-h-[60px]"
                    />
                    <div className="flex items-center gap-s2">
                      <button
                        type="button"
                        disabled={salvandoEndereco}
                        onClick={async () => {
                          if (!enderecoBuffer.trim()) return;
                          setSalvandoEndereco(true);
                          try {
                            const r = await atualizar(pedido.id, { endereco_entrega: enderecoBuffer.trim() });
                            const sucesso = r.latitude != null && r.longitude != null;
                            toast(
                              sucesso ? 'success' : 'error',
                              sucesso
                                ? `${pedido.numero_ticket}: endereço atualizado e geocodificado.`
                                : `${pedido.numero_ticket}: endereço salvo mas Nominatim não encontrou coords.`,
                            );
                            setEditandoEndereco(false);
                          } catch (err) {
                            toast('error', `Falha ao salvar — ${(err as Error).message}.`);
                          } finally {
                            setSalvandoEndereco(false);
                          }
                        }}
                        className="inline-flex items-center gap-s2 px-s4 py-[7px] rounded-sm bg-moss-500 border border-moss-400 text-fg-1 text-[12px] font-semibold hover:bg-moss-400 transition-colors duration-fast disabled:opacity-50"
                      >
                        <Check size={12} strokeWidth={1.8} />
                        {salvandoEndereco ? 'Salvando…' : 'Salvar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditandoEndereco(false); setEnderecoBuffer(''); }}
                        disabled={salvandoEndereco}
                        className="px-s4 py-[7px] rounded-sm border border-[var(--border)] text-fg-3 text-[12px] font-semibold hover:text-fg-1 hover:border-[var(--border-strong)] transition-colors duration-fast"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-s3">
                      <MapPin size={14} strokeWidth={1.5} className="mt-[2px] text-fg-3 shrink-0" />
                      <p className="flex-1 font-body text-[13px] text-fg-1 leading-relaxed">
                        {pedido.endereco_entrega}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setEditandoEndereco(true); setEnderecoBuffer(pedido.endereco_entrega || ''); }}
                        className="text-fg-3 hover:text-fg-1 transition-colors duration-fast p-[2px]"
                        aria-label="editar endereço"
                        title="Editar endereço"
                      >
                        <Pencil size={12} strokeWidth={1.6} />
                      </button>
                    </div>
                    {pedido.latitude != null && pedido.longitude != null ? (
                      <div className="flex items-center gap-s3 mt-s2">
                        <span className="font-mono text-[11px] text-fg-3">
                          {pedido.latitude.toFixed(4)}, {pedido.longitude.toFixed(4)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-s2 mt-s2">
                        <div className="flex items-start gap-s2 px-s3 py-s2 rounded-sm border border-amber-700 bg-[rgba(192,138,62,0.06)]">
                          <AlertTriangle size={12} strokeWidth={1.8} className="text-amber-500 mt-[2px] shrink-0" />
                          <span className="font-body text-[11.5px] text-amber-300 leading-snug">
                            Endereço sem coordenadas. Não entrará na rota.
                          </span>
                        </div>
                        <button
                          type="button"
                          disabled={regeocodificando}
                          onClick={async () => {
                            setRegeocodificando(true);
                            try {
                              const r = await atualizar(pedido.id, { regeocodificar: true });
                              const sucesso = r.latitude != null && r.longitude != null;
                              toast(
                                sucesso ? 'success' : 'error',
                                sucesso
                                  ? `${pedido.numero_ticket}: coords obtidas.`
                                  : `${pedido.numero_ticket}: Nominatim não encontrou coords. Edite o endereço.`,
                              );
                            } catch (err) {
                              toast('error', `Falha — ${(err as Error).message}.`);
                            } finally {
                              setRegeocodificando(false);
                            }
                          }}
                          className="self-start inline-flex items-center gap-s2 px-s3 py-[6px] rounded-sm border border-amber-700 text-amber-300 text-[11px] font-semibold uppercase tracking-[0.08em] hover:bg-[rgba(192,138,62,0.08)] transition-colors duration-fast disabled:opacity-50"
                        >
                          <RefreshCw size={11} strokeWidth={1.8} className={regeocodificando ? 'animate-spin' : ''} />
                          {regeocodificando ? 'Tentando…' : 'Tentar geocodificar'}
                        </button>
                        <div className="flex items-center gap-s2">
                          <input
                            value={mapaBuffer}
                            onChange={(e) => setMapaBuffer(e.target.value)}
                            placeholder="ou colar link do Google Maps"
                            className="flex-1 bg-input border border-[var(--border)] rounded-sm px-s3 py-[6px] font-body text-[11.5px] text-fg-1 outline-none focus:border-moss-400"
                          />
                          <button
                            type="button"
                            disabled={salvandoMapa || !mapaBuffer.trim()}
                            onClick={async () => {
                              setSalvandoMapa(true);
                              try {
                                const r = await atualizar(pedido.id, { mapa_link: mapaBuffer.trim() });
                                const ok = r.latitude != null && r.longitude != null;
                                toast(
                                  ok ? 'success' : 'error',
                                  ok
                                    ? `${pedido.numero_ticket}: coordenadas do link aplicadas.`
                                    : `${pedido.numero_ticket}: link não reconhecido (use ?q=lat,lng).`,
                                );
                                if (ok) setMapaBuffer('');
                              } catch (err) {
                                toast('error', `Falha — ${(err as Error).message}.`);
                              } finally {
                                setSalvandoMapa(false);
                              }
                            }}
                            className="inline-flex items-center px-s3 py-[6px] rounded-sm border border-moss-400 bg-moss-500 text-fg-1 text-[11px] font-semibold hover:bg-moss-400 disabled:opacity-40 transition-colors duration-fast"
                          >
                            Usar
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Bloco>

              {pedido.telefone && (
                <Bloco label="Contato">
                  <p className="flex items-center gap-s3 font-body text-[13px] text-fg-1">
                    <Phone size={14} strokeWidth={1.5} className="text-fg-3" />
                    {pedido.telefone}
                  </p>
                </Bloco>
              )}

              {pedido.observacoes && (
                <Bloco label="Observações">
                  <p className="font-body italic text-[13px] text-fg-2 leading-relaxed">
                    {pedido.observacoes}
                  </p>
                </Bloco>
              )}

              <Bloco label="Total">
                <Money valor={pedido.total} size="xl" />
                {pedido.pagamento && (
                  <span className="font-body text-[12px] text-fg-3 mt-s2 inline-block">
                    via {pedido.pagamento}
                  </span>
                )}
              </Bloco>

              <Bloco label="Tempo">
                <div className="flex items-center gap-s3 font-mono text-[12px] text-fg-2">
                  <Clock size={12} strokeWidth={1.5} className="text-fg-3" />
                  Criado {tempoRelativo(pedido.criado_em)}
                </div>
              </Bloco>
            </div>

            <footer className="flex flex-wrap gap-s3 px-s7 py-s5 border-t border-[var(--border-subtle)]">
              {acoes.map((a) => {
                const Icon = a.Icon;
                return (
                  <Button
                    key={a.status}
                    variant={a.tone}
                    onClick={async () => {
                      try {
                        await mudar(pedido.id, a.status);
                        toast('success', `${pedido.numero_ticket}: ${a.label.toLowerCase()}.`);
                      } catch {
                        toast('error', `Falha ao atualizar ${pedido.numero_ticket}.`);
                      }
                    }}
                    leading={<Icon size={14} strokeWidth={1.6} />}
                  >
                    {a.label}
                  </Button>
                );
              })}

              <div className="flex-1" />

              {confirmExcluirId === pedido.id ? (
                <div className="flex items-center gap-s2">
                  <span className="font-body text-[12px] text-fg-2">Excluir definitivamente?</span>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={excluindo}
                    onClick={async () => {
                      setExcluindo(true);
                      const ticket = pedido.numero_ticket;
                      try {
                        await excluir(pedido.id);
                        toast('success', `${ticket} excluído.`);
                      } catch (err) {
                        toast('error', `Falha ao excluir ${ticket} — ${(err as Error).message}.`);
                      } finally {
                        setExcluindo(false);
                        setConfirmExcluirId(null);
                      }
                    }}
                    leading={<Trash2 size={14} strokeWidth={1.6} />}
                  >
                    {excluindo ? 'Excluindo…' : 'Sim, excluir'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmExcluirId(null)} disabled={excluindo}>
                    Voltar
                  </Button>
                </div>
              ) : (
                <Button
                  variant="danger"
                  onClick={() => setConfirmExcluirId(pedido.id)}
                  leading={<Trash2 size={14} strokeWidth={1.6} />}
                >
                  Excluir
                </Button>
              )}
            </footer>
          </div>
        )}
      </aside>
    </>,
    document.body,
  );
}

function Bloco({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-s2">
      <span className="t-overline">{label}</span>
      {children}
    </section>
  );
}

function statusLabel(s: StatusBackend): string {
  const m: Record<StatusBackend, string> = {
    recebido: 'Pedido novo',
    pago: 'Aguardando pagamento',
    em_separacao: 'Em separação',
    em_rota: 'Em rota',
    entregue: 'Entregue',
    cancelado: 'Cancelado',
  };
  return m[s];
}

function statusTone(s: StatusBackend): 'amber' | 'moss' | 'sage' | 'soil' | 'mute' | 'petal' {
  if (s === 'recebido') return 'amber';
  if (s === 'pago') return 'moss';
  if (s === 'em_separacao') return 'sage';
  if (s === 'em_rota') return 'soil';
  if (s === 'entregue') return 'mute';
  return 'petal';
}

export default PedidoDetailDrawer;
